// What the pull request page reads from GitHub, as functions of the pull
// request and an AbortSignal: the runtime runs each in a lane of the page's
// Program (pr/program.ts) and aborts it when a newer read supersedes it, so
// none of them checks whether its answer is still wanted.
import { contentAt, graphql, GraphQLFailure, isSamlError, rest, restAll } from '../../api/gh';
import { withStacks } from '../../api/nativeStacks';
import type { Priority } from '../../api/scheduler';
import {
  COMMIT_CHECKS,
  PR_CHECKS,
  PR_DEPLOYMENTS,
  PR_QUERY,
  PR_STACK_QUERY,
  PR_STACK_QUERY_NO_STACKS,
  PR_THREADS_PAGE,
  STACK_POOL,
  PR_TIMELINE_PAGE,
  THREAD_COMMENTS_PAGE,
  replacedCommitsQuery,
  type Annotation,
  type CheckContext,
  type CheckContexts,
  type StackRowPr,
  type PageInfo,
  type PrFile,
  type PullRequestData,
  type ReplacedCommitsResult,
  type RepositoryData,
  type ReviewThread,
  type ThreadComment,
  type TimelineItem,
} from '../../api/queries';
import { generatedMatcher } from '../../logic/categorize';
import type { DeploymentCommit } from '../../logic/previews';
import { readChecks, readCommitChecks, readCommentsPage, readDeployments, readPr, readStack, readThreadsPage, readTimelinePage } from '../../api/answers';
import { poolAmong, type StackPool } from '../../logic/prStack';
import type { HeldStack } from '../stackHold';
import { parseReplaced, type ReplacedLookup } from '../../logic/pullTrack';
import { viewedStates } from '../githubViewed';
import type { RemoteViewed } from '../viewedSync.svelte';

/** Which pull request. */
export interface PrAt {
  owner: string;
  repo: string;
  number: number;
}

/** Timeline pages fetched beyond the first (newest) one; older items beyond are dropped. */
export const MAX_TIMELINE_PAGES = 20;
/** Check-context pages fetched beyond the first. */
export const MAX_CHECK_PAGES = 9;
/** Review-thread pages fetched beyond the first (100 threads each). */
export const MAX_THREAD_PAGES = 50;
/** Comment pages fetched beyond a thread's first. */
export const MAX_COMMENT_PAGES = 50;

/**
 * Whether paging goes on to `cursor`: a page that says more follows at a
 * null cursor, or at the one just read, would otherwise be read forever.
 */
export const advances = (cursor: string | null | undefined, last: string | null): cursor is string => !!cursor && cursor !== last;

/** One load of the pull request: everything its page shows from GitHub. */
export interface LoadedPr {
  viewer: string;
  repository: RepositoryData;
  pr: PullRequestData;
  threads: ReviewThread[];
  timeline: TimelineItem[];
  /** Older timeline items were dropped (more than the page cap). */
  truncated: boolean;
  /** GitHub's "Viewed" states of the first page, when they were asked for, and the cursor of the next page (null: none). */
  viewed: (RemoteViewed & { more: string | null }) | null;
}

/** The head's checks, as a checks refresh read them. */
export interface FreshChecks {
  head: string;
  commits: PullRequestData['commits'];
}

/** An annotation with the name of the check run it came from. */
export type CheckAnnotation = Annotation & { check: string };

const vars = (at: PrAt, extra: Record<string, unknown> = {}) => ({ owner: at.owner, repo: at.repo, number: at.number, ...extra });

/** What an error says; GitHub's SAML enforcement in its own words. */
export function message(e: unknown): string {
  if (e instanceof GraphQLFailure) {
    const saml = e.errors.find(isSamlError);
    if (saml) return `GitHub requires SAML single sign-on for this organization: ${saml.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}

/** `done` of `total`, where both are known. */
export interface Fraction {
  done: number;
  total: number;
}

/**
 * How far a load of the pull request is once its first answer is in: what
 * its next request reads, and what it holds so far.
 */
export interface PrProgress {
  reading: 'threads' | 'timeline' | 'checks';
  /** Review threads read so far. */
  threads: number;
  /** Review threads in all, as the first answer counts them; null (or absent) where it does not say. */
  threadsTotal?: number | null;
  /** Comments read of those the threads hold; null until a thread's further comments are read. */
  comments: Fraction | null;
  /** Timeline items read so far. */
  timeline: number;
  /** Timeline items in all, as the first answer counts them; null (or absent) where it does not say. */
  timelineTotal?: number | null;
  /** The files the pull request changes, as its first answer counts them (what the files' listing will read). */
  files?: number | null;
  /** The head's checks read, of those it has; null until they are read. */
  checks: Fraction | null;
}

/**
 * The pull request with every thread, comment, timeline page and check;
 * null when GitHub does not show it. With `viewed`, the first page of
 * GitHub's "Viewed" states comes with it, as read from when the load began.
 * `report` is told how far the load is before each request after the first
 * (never when the first answer holds everything). `early` is handed,
 * before the first of those requests, the pull request as its first answer
 * holds it: the first page of its threads (their first comments), its
 * newest timeline page and its first checks, so a view can open on it
 * while the rest is read (never when the first answer holds everything);
 * then again with the threads read so far before each further page of
 * them, so they appear as they land.
 */
export async function fetchPr(
  at: PrAt,
  viewed: boolean,
  signal: AbortSignal,
  report: (p: PrProgress) => void = () => {},
  early: (partial: LoadedPr) => void = () => {},
): Promise<LoadedPr | null> {
  const viewedAt = Date.now();
  // Every answer below is read as GitHub may send it, with holes (api/answers.ts): what leaves here is whole.
  const res = readPr(await graphql<unknown>(PR_QUERY, vars(at, { viewed }), { signal }));
  const found = res.repository?.pullRequest;
  const repository = res.repository;
  if (!repository || !found) return null;
  const progress: PrProgress = {
    reading: 'threads',
    threads: found.reviewThreads.nodes.length,
    threadsTotal: found.reviewThreads.totalCount ?? null,
    comments: null,
    timeline: found.timelineItems.nodes.length,
    timelineTotal: found.timelineItems.totalCount ?? null,
    files: found.changedFiles ?? null,
    checks: null,
  };
  // The first page only: the rest are read in the background, once per head.
  const { files, ...rest } = found;
  /** How many threads the view was last handed. */
  let handed = -1;
  const hand = (threads: readonly ReviewThread[]) => {
    handed = threads.length;
    early({ viewer: res.viewer.login, repository, pr: rest, threads: [...threads], timeline: found.timelineItems.nodes, truncated: false, viewed: null });
  };
  const say = (change: Partial<PrProgress>) => {
    if (handed < 0) hand(found.reviewThreads.nodes);
    report({ ...Object.assign(progress, change) });
  };
  // Threads past MAX_THREAD_PAGES (5,100 in all) are left out unsaid: no pull request comes near it, and the cap only stops a GitHub that pages without end.
  let threads = (await allThreads(at, found.reviewThreads, 'visible', signal, (read, sofar) => {
    say({ threads: read });
    if (sofar.length > handed) hand(sofar);
  }))!.nodes;
  if (threads.some((t) => t.comments.pageInfo?.hasNextPage)) {
    const comments = { done: threads.reduce((n, t) => n + t.comments.nodes.length, 0), total: threads.reduce((n, t) => n + t.comments.totalCount, 0) };
    say({ threads: threads.length, comments: { ...comments } });
    threads = await Promise.all(
      threads.map((t) =>
        withAllComments(t, signal, (read) => {
          comments.done += read;
          say({ comments: { ...comments } });
        }),
      ),
    );
  }
  const { nodes: timeline, truncated } = (await allTimeline(at, found.timelineItems, 'visible', signal, (read) =>
    say({ reading: 'timeline', threads: threads.length, timeline: read }),
  ))!;
  const viewedFiles: LoadedPr['viewed'] =
    viewed && files
      ? { head: found.headRefOid, at: viewedAt, states: viewedStates(files), more: files.pageInfo.hasNextPage ? files.pageInfo.endCursor : null }
      : null;
  let pr: PullRequestData = rest;
  const head = pr.commits.nodes[0];
  const rollup = head?.commit.statusCheckRollup;
  if (rollup) {
    const contexts = await allContexts(at, rollup.contexts, pr.headRefOid, 'visible', signal, (checks) =>
      say({ reading: 'checks', threads: threads.length, timeline: timeline.length, checks }),
    );
    pr = { ...pr, commits: { ...pr.commits, nodes: [{ ...head, commit: { ...head.commit, statusCheckRollup: { ...rollup, contexts } } }, ...pr.commits.nodes.slice(1)] } };
  }
  return { viewer: res.viewer.login, repository, pr, threads, timeline, truncated, viewed: viewedFiles };
}

/**
 * What is already known of the pull request's stack, without asking
 * GitHub: its stack and when that was read, that it is on its own (as of
 * when), or nothing (null).
 */
export type KeptStack = { kind: 'stack'; pool: StackPool; since: number } | { kind: 'alone'; since: number } | null;

/** The dashboard's rows as its saved copy holds them: every section's and the stack members read by ID, and when the oldest were read. */
export interface SavedRows {
  prs: StackRowPr[];
  hidden: string[];
  at: number;
}

/**
 * What the app already knows of the pull request's stack: the stack
 * another member's page held (`held`), when the pull request is in it;
 * else what the dashboard's saved rows (`saved`) say, a stack or that it is
 * on its own. Null when neither holds the pull request.
 */
export async function keptStack(at: PrAt, held: HeldStack | null, saved: () => Promise<SavedRows | null>): Promise<KeptStack> {
  const repo = `${at.owner}/${at.repo}`;
  if (held && held.repo.toLowerCase() === repo.toLowerCase()) {
    const pool = poolAmong(held.prs, held.hidden, repo, at.number, held.source, held.cut);
    if (pool && pool !== 'alone') return { kind: 'stack', pool, since: held.since };
  }
  const rows = await saved().catch(() => null);
  const found = rows && poolAmong(rows.prs, rows.hidden, repo, at.number, 'dashboard');
  if (!rows || !found) return null;
  return found === 'alone' ? { kind: 'alone', since: rows.at } : { kind: 'stack', pool: found, since: rows.at };
}

/**
 * What GitHub says the pull request's stack is found among, in one
 * request (PR_STACK_QUERY): its native stack's members, and the
 * repository's STACK_POOL most recently updated open pull requests, by
 * whose branches the dashboard's rule finds a stack GitHub does not name.
 * Background work for the scheduler: it never delays what the page waits
 * on. Null when GitHub does not show the pull request.
 */
export async function fetchStack(at: PrAt, signal: AbortSignal): Promise<StackPool | null> {
  // Read as GitHub may send it, with holes (api/answers.ts): every row left is whole.
  const res = readStack(await withStacks<unknown>(PR_STACK_QUERY, PR_STACK_QUERY_NO_STACKS, vars(at, { first: STACK_POOL }), { priority: 'background', signal }));
  const own = res?.repository.pullRequest;
  if (!res || !own) return null;
  const open = res.repository.pullRequests;
  const prs = new Map<string, StackRowPr>();
  for (const p of open.nodes) if (p) prs.set(p.id, { ...p, stack: p.stack ?? null, stackEntry: p.stackEntry ?? null });
  const stack = own.stack && own.stackEntry ? own.stack : null;
  // The members' own stack fields are their entries': the stack, and each one's position in it.
  const named = stack && { id: stack.id, size: stack.size, entries: { nodes: stack.entries.nodes.map((e) => e && { position: e.position, pullRequest: e.pullRequest && { id: e.pullRequest.id, number: e.pullRequest.number } }) } };
  for (const e of stack?.entries.nodes ?? []) if (e?.pullRequest) prs.set(e.pullRequest.id, { ...e.pullRequest, stack: named, stackEntry: { position: e.position } });
  return {
    source: 'github',
    prs: [...prs.values()],
    own: { stack: named, stackEntry: named ? own.stackEntry! : null },
    hidden: [],
    cut: open.totalCount > res.listed,
  };
}

/** Review threads read, and whether GitHub has more than were read. */
export interface ThreadsRead {
  nodes: ReviewThread[];
  truncated: boolean;
}

/**
 * Every review thread: `first` (the page PR_QUERY holds) or, when null,
 * PR_THREADS_PAGE's first page, then MAX_THREAD_PAGES further pages at
 * most. `more` is told before each further page how many are read, and
 * those threads. Null when GitHub does not show the pull request.
 */
export async function allThreads(
  at: PrAt,
  first: PullRequestData['reviewThreads'] | null,
  priority: Priority,
  signal: AbortSignal,
  more: (read: number, sofar: readonly ReviewThread[]) => void = () => {},
): Promise<ThreadsRead | null> {
  const start = first ?? readThreadsPage(await graphql<unknown>(PR_THREADS_PAGE, vars(at, { after: null }), { priority, signal }));
  if (!start) return null;
  let nodes = start.nodes;
  let page = start.pageInfo;
  let last: string | null = null;
  for (let i = 0; page.hasNextPage && advances(page.endCursor, last) && i < MAX_THREAD_PAGES; i++) {
    more(nodes.length, nodes);
    last = page.endCursor;
    const next = readThreadsPage(await graphql<unknown>(PR_THREADS_PAGE, vars(at, { after: last }), { priority, signal }));
    if (!next) break;
    nodes = nodes.concat(next.nodes);
    page = next.pageInfo;
  }
  return { nodes, truncated: page.hasNextPage };
}

/**
 * The timeline, oldest item first: `first` (the page PR_QUERY holds) or,
 * when null, PR_TIMELINE_PAGE's newest page, then MAX_TIMELINE_PAGES older
 * pages at most; `truncated` says older items were dropped. `more` is told
 * before each further page how many are read. Null when GitHub does not
 * show the pull request.
 */
export async function allTimeline(
  at: PrAt,
  first: PullRequestData['timelineItems'] | null,
  priority: Priority,
  signal: AbortSignal,
  more: (read: number) => void = () => {},
): Promise<{ nodes: TimelineItem[]; truncated: boolean } | null> {
  const start = first ?? readTimelinePage(await graphql<unknown>(PR_TIMELINE_PAGE, vars(at, { before: null }), { priority, signal }));
  if (!start) return null;
  // Timeline pages run newest first, so a cap drops the oldest items.
  let nodes = start.nodes;
  let page = start.pageInfo;
  let last: string | null = null;
  for (let i = 0; page.hasPreviousPage && advances(page.startCursor, last) && i < MAX_TIMELINE_PAGES; i++) {
    more(nodes.length);
    last = page.startCursor;
    const next = readTimelinePage(await graphql<unknown>(PR_TIMELINE_PAGE, vars(at, { before: last }), { priority, signal }));
    if (!next) break;
    nodes = next.nodes.concat(nodes);
    page = next.pageInfo;
  }
  return { nodes, truncated: page.hasPreviousPage };
}

/**
 * A thread with every comment (the first page holds 50), MAX_COMMENT_PAGES
 * further pages at most; `read` is told how many each further page brought.
 */
export async function withAllComments(
  t: ReviewThread,
  signal: AbortSignal,
  read: (comments: number) => void = () => {},
  priority: Priority = 'visible',
): Promise<ReviewThread> {
  let page: PageInfo | undefined = t.comments.pageInfo;
  if (!page?.hasNextPage) return t;
  let nodes: ThreadComment[] = t.comments.nodes;
  let last: string | null = null;
  for (let i = 0; page?.hasNextPage && advances(page.endCursor, last) && i < MAX_COMMENT_PAGES; i++) {
    last = page.endCursor;
    const more = readCommentsPage(await graphql<unknown>(THREAD_COMMENTS_PAGE, { id: t.id, after: last }, { priority, signal }));
    if (!more) break;
    nodes = nodes.concat(more.nodes);
    page = more.pageInfo;
    read(more.nodes.length);
  }
  return { ...t, comments: { ...t.comments, nodes, pageInfo: page } };
}

/**
 * Every check context for the head commit (first page plus further pages).
 * `reading` is told, before each further page, how many are read of those
 * the commit has (null when GitHub gave no count).
 */
export async function allContexts(
  at: PrAt,
  first: CheckContexts,
  head: string,
  priority: Priority,
  signal: AbortSignal,
  reading: (read: Fraction | null) => void | Promise<void> = () => {},
): Promise<CheckContexts> {
  let nodes = first.nodes;
  let page = first.pageInfo;
  let last: string | null = null;
  for (let i = 0; page?.hasNextPage && advances(page.endCursor, last) && i < MAX_CHECK_PAGES; i++) {
    await reading(first.totalCount === undefined ? null : { done: nodes.length, total: first.totalCount });
    last = page.endCursor;
    const pr = readChecks(await graphql<unknown>(PR_CHECKS, vars(at, { after: last }), { priority, signal }));
    const ctx = pr?.commits.nodes[0]?.commit.statusCheckRollup?.contexts;
    if (!pr || pr.headRefOid !== head || !ctx) break;
    nodes = nodes.concat(ctx.nodes);
    page = ctx.pageInfo;
  }
  return { ...first, nodes, pageInfo: page };
}

/**
 * A commit's checks, any commit's (COMMIT_CHECKS: its statusCheckRollup,
 * check runs and commit statuses together), paged as `allContexts` pages a
 * pull request's head, MAX_CHECK_PAGES further pages at most; `ask` is told
 * before each request (a budget may refuse it by throwing). Null when
 * GitHub does not show the commit; `contexts` null when it has no checks.
 */
export async function commitRollup(
  owner: string,
  repo: string,
  oid: string,
  opts: { priority: Priority; signal: AbortSignal },
  ask: () => Promise<void> = async () => {},
): Promise<{ state: string | null; contexts: CheckContexts | null } | null> {
  const read = async (after: string | null) => {
    await ask();
    return readCommitChecks(await graphql<unknown>(COMMIT_CHECKS, { owner, repo, oid, after }, opts));
  };
  const first = await read(null);
  if (!first) return null;
  const rollup = first.statusCheckRollup;
  if (!rollup) return { state: null, contexts: null };
  let { nodes, pageInfo } = rollup.contexts;
  let last: string | null = null;
  for (let i = 0; pageInfo?.hasNextPage && advances(pageInfo.endCursor, last) && i < MAX_CHECK_PAGES; i++) {
    last = pageInfo.endCursor;
    const more = (await read(last))?.statusCheckRollup?.contexts;
    if (!more) break;
    nodes = nodes.concat(more.nodes);
    pageInfo = more.pageInfo;
  }
  return { state: rollup.state, contexts: { ...rollup.contexts, nodes, pageInfo } };
}

/** Only the head's checks (after a check or status notice); null when GitHub does not show the pull request. */
export async function fetchChecks(at: PrAt, signal: AbortSignal): Promise<FreshChecks | null> {
  const fresh = readChecks(await graphql<unknown>(PR_CHECKS, vars(at, { after: null }), { priority: 'background', signal }));
  if (!fresh) return null;
  let commits = fresh.commits;
  const head = commits.nodes[0];
  const rollup = head?.commit.statusCheckRollup;
  if (rollup) {
    const contexts = await allContexts(at, rollup.contexts, fresh.headRefOid, 'background', signal);
    commits = { ...commits, nodes: [{ ...head, commit: { ...head.commit, statusCheckRollup: { ...rollup, contexts } } }, ...commits.nodes.slice(1)] };
  }
  return { head: fresh.headRefOid, commits };
}

/** The changed files, with the head they are the files of (null: not known). */
export interface LoadedFiles {
  head: string | null;
  files: PrFile[];
}

/** Files per page of the listing. */
const FILES_PAGE = 100;
/** The most files the listing holds: GitHub lists no more than 3,000, and `restAll` reads 30 pages at most. */
export const MAX_FILES = 3000;

/** Listings tried before giving up on a pull request whose head keeps moving under them. */
export const FILES_TRIES = 3;

/**
 * The changed files (GitHub's pulls/:n/files), with the head GitHub had
 * for the pull request both before and after the pages were read: the
 * list names no head, and its pages are read one by one, so a push while
 * they are read could mix two heads' files. When the head moved meanwhile
 * the files are listed again, FILES_TRIES times at most; then it fails
 * rather than stamp a head it could not confirm. When GitHub names no
 * head, the head is null. `listed` is told how many files the listing
 * under way holds after each of its pages, and those files (a copy: not
 * yet confirmed against the head, so provisional). Every read goes at
 * `priority`.
 *
 * The head is read as the pull request's REST resource, revalidated by
 * its ETag (`rest`): GitHub does not count a read that finds it unchanged
 * (a 304), so the check costs a request only when the pull request
 * changed since it was last read, and never a GraphQL point.
 */
export async function fetchFiles(
  at: PrAt,
  signal: AbortSignal,
  listed: (count: number, files: readonly PrFile[]) => void = () => {},
  priority: Priority = 'visible',
): Promise<LoadedFiles> {
  const pull = `repos/${at.owner}/${at.repo}/pulls/${at.number}`;
  const headNow = async () => (await rest<{ head?: { sha?: string } }>(pull, { priority, signal })).head?.sha ?? null;
  let before = await headNow();
  for (let tries = 1; ; tries++) {
    const files = await restAll<PrFile>(`${pull}/files?per_page=${FILES_PAGE}`, { priority, signal, onPage: (n, sofar) => listed(n, sofar.slice()) });
    const after = await headNow();
    if (after === before) return { head: after, files };
    if (tries >= FILES_TRIES) throw new Error('This pull request kept changing while its files were listed. Try again in a moment.');
    before = after;
  }
}

/** A completed check run's annotations. */
export async function fetchAnnotations(at: PrAt, run: CheckContext, signal: AbortSignal): Promise<CheckAnnotation[]> {
  const list = await restAll<Annotation>(`repos/${at.owner}/${at.repo}/check-runs/${run.databaseId}/annotations?per_page=100`, {
    priority: 'background',
    signal,
  });
  return list.map((a) => ({ ...a, check: run.name ?? 'check' }));
}

/**
 * Deployments of the pull request's latest commits, read as GitHub may
 * send them (api/answers.ts): a commit whose deployments it withheld (a
 * partial error, for a token that may not read them) has none.
 */
export async function fetchDeployments(at: PrAt, signal: AbortSignal): Promise<DeploymentCommit[]> {
  return readDeployments(await graphql<unknown>(PR_DEPLOYMENTS, vars(at), { signal }));
}

/** The commits the force-pushes before `befores` replaced, against `base`. */
export async function fetchReplaced(at: PrAt, base: string, befores: string[], signal: AbortSignal): Promise<ReplacedLookup> {
  const v: Record<string, unknown> = vars(at, { base });
  befores.forEach((b, i) => (v['b' + i] = b));
  return parseReplaced(await graphql<ReplacedCommitsResult>(replacedCommitsQuery(befores.length), v, { priority: 'background', signal }), befores);
}

/** Which paths the repository's `.gitattributes` at `head` marks generated. */
export async function fetchGenerated(at: PrAt, head: string, signal: AbortSignal): Promise<(path: string) => boolean> {
  return generatedMatcher(await contentAt(at.owner, at.repo, '.gitattributes', head, { signal }));
}
